import { RhButton, RhCard, RhIcon, RhInputFormik, Yup } from "@rhythm-ui/react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import { Form, Formik } from "formik";
import React, { useState } from "react";

import api from "@/services/api";

import Button from "@/components/utils/Button";

type JobNote = {
  id: number;
  note: string;
  created_ts: string;
  user_name: string;
  edited?: boolean;
};

const JobNoteItem = ({
  noteItem,
  jobId,
}: {
  noteItem: JobNote;
  jobId: number | null;
}) => {
  const [isEdit, setIsEdit] = useState(false);
  const queryClient = useQueryClient();

  const validationSchema = Yup.object().shape({
    note: Yup.string().required("Required"),
  });

  const isEditable = dayjs().diff(noteItem.created_ts, "minute") < 30;

  const patchJobNote = useMutation({
    mutationFn: (note: string) => {
      if (!jobId) return Promise.reject(new Error("Job ID is required"));
      return api.company.patchJobRequirementNote(jobId.toString(), {
        note_id: noteItem.id,
        note,
      });
    },
    onSuccess() {
      queryClient.invalidateQueries({
        queryKey: ["JOBS", "ITEM", { jobId }],
      });
      setIsEdit(false);
    },
  });

  if (isEdit) {
    return (
      <RhCard className="px-tsm py-tnano">
        <Formik
          initialValues={{ note: noteItem.note }}
          onSubmit={(formData) => patchJobNote.mutate(formData.note)}
          validationSchema={validationSchema}
        >
          <Form>
            <h4>Notes</h4>
            <RhInputFormik
              type="textarea"
              name="note"
              className="my-tsm w-full"
              rows={3}
              placeholder="Edit notes here"
            />
            <div className="flex justify-end gap-tnano">
              <RhButton
                layout="link"
                type="button"
                variant="secondary"
                onClick={() => setIsEdit(false)}
              >
                Cancel
              </RhButton>
              <Button loading={patchJobNote.isLoading} type="submit">
                Save
              </Button>
            </div>
          </Form>
        </Formik>
      </RhCard>
    );
  }

  return (
    <>
      <div className="flex h-[32px] items-center justify-between">
        <div className="flex items-center">
          <p className="body-small">
            {noteItem.user_name} <span className="text-gray-600">added a</span>{" "}
            note
            {noteItem.edited && (
              <span className="text-gray-600"> (Edited)</span>
            )}
          </p>
          {isEditable && (
            <RhButton
              size="sm"
              layout="link"
              onClick={() => setIsEdit(true)} // no need for editNoteHandler anymore
            >
              <RhIcon icon="tabler:pencil" />
            </RhButton>
          )}
        </div>
        <p className="body-small text-gray-600">
          {dayjs(noteItem.created_ts).format("DD MMM YYYY, hh:mm a")}
        </p>
      </div>

      <RhCard className="px-tsm py-tnano">
        <p className="whitespace-pre-line text-gray-900">
          {noteItem.note ?? "-"}
        </p>
      </RhCard>
    </>
  );
};

export default JobNoteItem;
